import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Edit3, Check, Paintbrush, Undo2, Redo2, X, ShieldCheck, Loader2, Crosshair, Trash2, Code2, Copy, CheckCheck, ChevronDown, HardDrive, Globe, Sparkles, Wand2, ChevronsUp, ChevronsDown, Maximize2} from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { toast } from 'sonner';  // r117: off-canvas drop warning

// CSS properties to read from computed styles when an element is selected
const CSS_PROPS_TO_READ = [
  'color', 'background-color', 'background', 'font-size', 'font-weight',
  'font-family', 'text-align', 'line-height', 'letter-spacing',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'width', 'height', 'border-top-width', 'border-top-style', 'border-top-color',
  'border-radius', 'display', 'flex-direction', 'align-items',
  'justify-content', 'gap', 'opacity', 'box-shadow',
  'fill', 'object-fit',  // r144b: the panel's icon/image controls read these
];

// Script injected into the iframe for element selection + resize handles in style mode
const STYLE_PICKER_SCRIPT = `
(function() {
  if (window.__stylePickerActive) return;
  window.__stylePickerActive = true;
  // Kill switch: removing the <script> element does NOT unbind these
  // capture-phase document listeners — after Done they kept selecting and
  // dragging in view mode (the iframe is only rewritten when html_source
  // actually changes, and autosave usually already persisted it unchanged).
  // Every handler checks this window flag; cleanupMode() flips it on exit,
  // enableStylePicker() re-arms it.
  window.__editorDisabled = false;
  // Generation counter: a re-injected picker bumps it; handlers from OLDER
  // injections (their listeners survive script removal) self-disable instead
  // of stacking duplicate outlines/handles per enable cycle.
  var myGen = window.__editorGen = (window.__editorGen || 0) + 1;
  function dead() { return window.__editorDisabled === true || window.__editorGen !== myGen; }

  var selected = null;
  var hovered = null;
  var handles = [];
  var guideLines = [];
  var siblingRects = [];
  var resizing = false;
  var dragging = false;
  var pendingDrag = null;  // armed on mousedown; promotes to a drag past a move threshold
  var justDragged = false;
  var resizeHandle = null;
  var startX = 0, startY = 0, startW = 0, startH = 0;
  var dragStartLeft = 0, dragStartTop = 0;
  var dragPrevZ = '';
  var dragPrevComputedZ = 'auto';
  var liftedAncestors = [];
  // r122: the clipping container for the element currently being dragged,
  // resolved once on drag start. Recomputing it per mousemove would read
  // layout in the middle of a gesture that is writing to it.
  var dragClip = null;
  var revealedClips = [];

  // A z-lift on the dragged element can't escape an ancestor that creates its
  // own stacking context (transform/opacity/filter/positioned z — common in
  // generated slides with entrance animations). Temporarily lift those
  // ancestors too while dragging, and restore them on drop.
  /**
   * r119: is any FOREIGN painting element covering this one?
   *
   * Samples a grid, not a single point. The centre-only probe answered "no"
   * for a title whose right half was under a photo, because its centre was in
   * clear space — which is precisely why the user still saw elements land
   * behind images after r117. Nine points cost nine hit tests; a wrong answer
   * costs the user a visibly broken slide.
   *
   * Returns true on error: assuming "covered" keeps the old lifting behaviour,
   * which is the safe direction.
   */
  /**
   * r120: which ancestor is CLIPPING this element, if any?
   *
   * A clip and an occlusion look identical to a user ("it went behind
   * something") but have opposite fixes: stacking cannot rescue a clip, only
   * moving the element back inside its container or taking it out of that
   * container can. Three rounds of z-index work never touched this because
   * nothing was measuring it.
   *
   * Returns {el, name, lostPct} for the nearest ancestor whose padding box cuts
   * the element, or null.
   */
  /**
   * r122: reveal ONLY the clipping ancestors of the element being dragged, for
   * the duration of the drag.
   *
   * Edit mode used to set overflow:visible on html and body for the whole
   * session. That made the slide canvas horizontally scrollable - visible as a
   * scrollbar under the slide, and it shifted content sideways under the
   * pointer, which is a large part of why dragging felt unpredictable. The
   * canvas now stays a fixed 1280x720 and only the containers that would hide
   * the dragged element are opened, then closed again on drop.
   */
  function revealClipsFor(el) {
    revealedClips = [];
    var node = el ? el.parentElement : null;
    while (node && node !== document.body && node !== document.documentElement) {
      var cs = window.getComputedStyle(node);
      if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
        revealedClips.push({ el: node, prev: node.style.overflow || '' });
        node.style.overflow = 'visible';
      }
      node = node.parentElement;
    }
    // r125: show the boundary being crossed WHILE it matters — during this
    // drag, for this element's own containers — instead of dashing every
    // container on the slide all session (read as spurious borders).
    try {
      var layer = document.createElement('div');
      layer.className = '__clip-hints';
      layer.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:2147483646;';
      for (var h = 0; h < revealedClips.length; h++) {
        var hr = revealedClips[h].el.getBoundingClientRect();
        var hb = document.createElement('div');
        hb.className = '__clip-hint';
        hb.style.cssText = 'position:absolute;border:1px dashed rgba(99,102,241,0.45);'
          + 'left:' + Math.round(hr.left) + 'px;top:' + Math.round(hr.top) + 'px;'
          + 'width:' + Math.round(hr.width) + 'px;height:' + Math.round(hr.height) + 'px;';
        layer.appendChild(hb);
      }
      if (revealedClips.length) document.body.appendChild(layer);
    } catch (hlErr) {}
  }

  function restoreRevealedClips() {
    try {
      var hlayers = document.querySelectorAll('.__clip-hints');
      for (var hl = 0; hl < hlayers.length; hl++) hlayers[hl].remove();
    } catch (hrErr) {}
    for (var i = 0; i < revealedClips.length; i++) {
      var rc = revealedClips[i];
      if (rc.prev) rc.el.style.overflow = rc.prev;
      else rc.el.style.removeProperty('overflow');
    }
    revealedClips = [];
  }

  function clippingAncestorOf(el) {
    try {
      var r = el.getBoundingClientRect();
      var area = r.width * r.height;
      if (!area) return null;
      var node = el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        var cs = window.getComputedStyle(node);
        var clipsX = cs.overflowX !== 'visible';
        var clipsY = cs.overflowY !== 'visible';
        if (clipsX || clipsY) {
          var cr = node.getBoundingClientRect();
          var vx0 = clipsX ? Math.max(r.left, cr.left) : r.left;
          var vx1 = clipsX ? Math.min(r.right, cr.right) : r.right;
          var vy0 = clipsY ? Math.max(r.top, cr.top) : r.top;
          var vy1 = clipsY ? Math.min(r.bottom, cr.bottom) : r.bottom;
          var vis = Math.max(0, vx1 - vx0) * Math.max(0, vy1 - vy0);
          var lost = 1 - (vis / area);
          if (lost > 0.08) {
            var nm = (node.tagName || 'div').toLowerCase();
            if (node.className) nm += '.' + String(node.className).split(' ')[0];
            return { el: node, name: nm, lostPct: Math.round(lost * 100) };
          }
        }
        node = node.parentElement;
      }
      return null;
    } catch (e) { return null; }
  }

  function coveredByForeignPaint(el) {
    try {
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      var xs = [0.08, 0.5, 0.92], ys = [0.08, 0.5, 0.92];
      for (var i = 0; i < xs.length; i++) {
        for (var j = 0; j < ys.length; j++) {
          var px = Math.min(1279, Math.max(1, r.left + r.width * xs[i]));
          var py = Math.min(719, Math.max(1, r.top + r.height * ys[j]));
          var hit = document.elementFromPoint(px, py);
          if (!hit || hit === el || el.contains(hit) || hit.contains(el)) continue;
          if (hit.className && String(hit.className).indexOf('__') === 0) continue;
          var cs = window.getComputedStyle(hit);
          if (cs.visibility === 'hidden' || parseFloat(cs.opacity || '1') < 0.1) continue;
          var tag = (hit.tagName || '').toUpperCase();
          var paints = tag === 'IMG' || tag === 'CANVAS' || tag === 'SVG' ||
                       tag === 'VIDEO' || tag === 'PICTURE' ||
                       cs.backgroundImage !== 'none' ||
                       cs.backgroundColor !== 'rgba(0, 0, 0, 0)';
          if (!paints) {
            // own text also paints
            for (var t = 0; t < hit.childNodes.length; t++) {
              var nd = hit.childNodes[t];
              if (nd.nodeType === 3 && (nd.textContent || '').trim()) { paints = true; break; }
            }
          }
          if (paints) return true;
        }
      }
      return false;
    } catch (e) { return true; }
  }

  function liftStackingAncestors(el) {
    var node = el.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      var cs = window.getComputedStyle(node);
      var createsCtx = (cs.position !== 'static' && cs.zIndex !== 'auto') ||
        cs.transform !== 'none' || parseFloat(cs.opacity) < 1 ||
        (cs.filter && cs.filter !== 'none');
      if (createsCtx) {
        var entry = { el: node, z: node.style.zIndex || '', pos: false };
        if (cs.position === 'static') { entry.pos = true; node.style.position = 'relative'; }
        node.style.zIndex = '9999';
        liftedAncestors.push(entry);
      }
      node = node.parentElement;
    }
  }

  function restoreLiftedAncestors() {
    for (var i = 0; i < liftedAncestors.length; i++) {
      var la = liftedAncestors[i];
      if (la.z !== '') la.el.style.zIndex = la.z;
      else la.el.style.removeProperty('z-index');
      if (la.pos) la.el.style.removeProperty('position');
    }
    liftedAncestors = [];
  }
  var SNAP_THRESHOLD = 5;

  function getPath(el) {
    var parts = [];
    while (el && el !== document.body && el !== document.documentElement) {
      var tag = el.tagName.toLowerCase();
      var cls = el.className && typeof el.className === 'string' ? '.' + el.className.trim().replace(/\\s+/g, '.') : '';
      parts.unshift(tag + cls);
      el = el.parentElement;
    }
    return parts.join(' > ');
  }

  function removeHandles() {
    handles.forEach(function(h) { if (h.parentNode) h.parentNode.removeChild(h); });
    handles = [];
  }

  function createHandles(el) {
    removeHandles();
    var rect = el.getBoundingClientRect();
    var positions = [
      { cursor: 'nw-resize', x: rect.left - 4, y: rect.top - 4, dx: -1, dy: -1 },
      { cursor: 'n-resize',  x: rect.left + rect.width / 2 - 4, y: rect.top - 4, dx: 0, dy: -1 },
      { cursor: 'ne-resize', x: rect.right - 4, y: rect.top - 4, dx: 1, dy: -1 },
      { cursor: 'w-resize',  x: rect.left - 4, y: rect.top + rect.height / 2 - 4, dx: -1, dy: 0 },
      { cursor: 'e-resize',  x: rect.right - 4, y: rect.top + rect.height / 2 - 4, dx: 1, dy: 0 },
      { cursor: 'sw-resize', x: rect.left - 4, y: rect.bottom - 4, dx: -1, dy: 1 },
      { cursor: 's-resize',  x: rect.left + rect.width / 2 - 4, y: rect.bottom - 4, dx: 0, dy: 1 },
      { cursor: 'se-resize', x: rect.right - 4, y: rect.bottom - 4, dx: 1, dy: 1 },
    ];
    positions.forEach(function(pos) {
      var h = document.createElement('div');
      h.className = '__resize-handle';
      h.style.cssText = 'position:fixed;width:8px;height:8px;background:#6366f1;border:1px solid white;border-radius:1px;z-index:99999;cursor:' + pos.cursor + ';';
      h.style.left = pos.x + 'px';
      h.style.top = pos.y + 'px';
      h.dataset.dx = pos.dx;
      h.dataset.dy = pos.dy;
      document.body.appendChild(h);
      handles.push(h);
    });
  }

  // Cheap reposition of the existing 8 handles during drag/resize — avoids the
  // DOM thrash (remove + recreate 8 nodes every mousemove) that made it feel laggy.
  function updateHandlePositions(el) {
    if (handles.length !== 8) { createHandles(el); return; }
    var rect = el.getBoundingClientRect();
    var pts = [
      [rect.left - 4, rect.top - 4],
      [rect.left + rect.width / 2 - 4, rect.top - 4],
      [rect.right - 4, rect.top - 4],
      [rect.left - 4, rect.top + rect.height / 2 - 4],
      [rect.right - 4, rect.top + rect.height / 2 - 4],
      [rect.left - 4, rect.bottom - 4],
      [rect.left + rect.width / 2 - 4, rect.bottom - 4],
      [rect.right - 4, rect.bottom - 4],
    ];
    for (var i = 0; i < 8; i++) {
      handles[i].style.left = pts[i][0] + 'px';
      handles[i].style.top = pts[i][1] + 'px';
    }
  }

  function removeGuides() {
    guideLines.forEach(function(g) { if (g.parentNode) g.parentNode.removeChild(g); });
    guideLines = [];
  }

  function renderGuides(guides) {
    removeGuides();
    var seen = {};
    guides.forEach(function(g) {
      var key = g.axis + ':' + Math.round(g.pos);
      if (seen[key]) return;
      seen[key] = true;
      var line = document.createElement('div');
      line.className = '__guide-line';
      if (g.axis === 'v') {
        line.style.cssText = 'position:fixed;left:'+g.pos+'px;top:0;width:1px;height:720px;background:#ef4444;z-index:99998;pointer-events:none;';
      } else {
        line.style.cssText = 'position:fixed;left:0;top:'+g.pos+'px;width:1280px;height:1px;background:#ef4444;z-index:99998;pointer-events:none;';
      }
      document.body.appendChild(line);
      guideLines.push(line);
    });
  }

  function collectSiblingRects() {
    siblingRects = [];
    var allEls = document.querySelectorAll('body *:not(.__resize-handle):not(.__guide-line)');
    allEls.forEach(function(el) {
      if (el === selected || selected.contains(el) || el.contains(selected)) return;
      if (el.offsetWidth < 5 || el.offsetHeight < 5) return;
      var r = el.getBoundingClientRect();
      siblingRects.push({ left: r.left, right: r.right, top: r.top, bottom: r.bottom, centerX: r.left + r.width / 2, centerY: r.top + r.height / 2 });
    });
    // Slide edges and center
    siblingRects.push({ left: 0, right: 1280, top: 0, bottom: 720, centerX: 640, centerY: 360 });
  }

  // r119: coalesce snap+guide work into one animation frame. It ran on every
  // mousemove, and it removes and recreates guide nodes each time — a write,
  // then a rect read, then more writes, per event. At pointer rate that is the
  // "switch between dragging and selecting is laggy" the user reports.
  var snapRaf = 0;
  function scheduleAlignmentAndSnap() {
    if (snapRaf) return;
    snapRaf = requestAnimationFrame(function () {
      snapRaf = 0;
      checkAlignmentAndSnap();   // the real work; scheduling here would recurse
    });
  }

  function checkAlignmentAndSnap() {
    if (!selected) return;
    var rect = selected.getBoundingClientRect();
    var selCX = rect.left + rect.width / 2;
    var selCY = rect.top + rect.height / 2;
    var guides = [];
    var snapX = null, snapY = null;
    var bestDx = SNAP_THRESHOLD, bestDy = SNAP_THRESHOLD;

    siblingRects.forEach(function(sib) {
      // Vertical guides (x-axis)
      var xChecks = [
        { sel: rect.left, ref: sib.left }, { sel: rect.left, ref: sib.right },
        { sel: rect.right, ref: sib.right }, { sel: rect.right, ref: sib.left },
        { sel: selCX, ref: sib.centerX }
      ];
      xChecks.forEach(function(c) {
        var d = Math.abs(c.sel - c.ref);
        if (d < SNAP_THRESHOLD) {
          guides.push({ axis: 'v', pos: c.ref });
          if (d < bestDx) { bestDx = d; snapX = c.ref - c.sel; }
        }
      });
      // Horizontal guides (y-axis)
      var yChecks = [
        { sel: rect.top, ref: sib.top }, { sel: rect.top, ref: sib.bottom },
        { sel: rect.bottom, ref: sib.bottom }, { sel: rect.bottom, ref: sib.top },
        { sel: selCY, ref: sib.centerY }
      ];
      yChecks.forEach(function(c) {
        var d = Math.abs(c.sel - c.ref);
        if (d < SNAP_THRESHOLD) {
          guides.push({ axis: 'h', pos: c.ref });
          if (d < bestDy) { bestDy = d; snapY = c.ref - c.sel; }
        }
      });
    });

    // Apply snap correction
    if (snapX !== null) {
      var curLeft = parseInt(selected.style.left) || 0;
      selected.style.left = (curLeft + snapX) + 'px';
    }
    if (snapY !== null) {
      var curTop = parseInt(selected.style.top) || 0;
      selected.style.top = (curTop + snapY) + 'px';
    }

    renderGuides(guides);
  }

  function notifyParent() {
    if (!selected) return;
    var computed = window.getComputedStyle(selected);
    var props = ${JSON.stringify(CSS_PROPS_TO_READ)};
    var computedStyles = {};
    props.forEach(function(p) { computedStyles[p] = computed.getPropertyValue(p); });
    window.parent.postMessage({
      type: 'ELEMENT_SELECTED',
      element: {
        tag: selected.tagName.toLowerCase(),
        id: selected.id || null,
        classList: Array.from(selected.classList),
        computedStyles: computedStyles,
        inlineStyle: selected.getAttribute('style') || '',
        path: getPath(selected),
        textContent: (selected.textContent || '').substring(0, 60),
      }
    }, '*');
  }

  document.addEventListener('mouseover', function(e) {
    if (dead()) return;
    if (resizing) return;
    if (e.target.classList && e.target.classList.contains('__resize-handle')) return;
    if (e.target === selected || e.target === document.body || e.target === document.documentElement) return;
    if (hovered && hovered !== selected) {
      hovered.style.outline = '';
      hovered.style.outlineOffset = '';
    }
    hovered = e.target;
    if (hovered !== selected) {
      hovered.style.outline = '1px dashed rgba(99,102,241,0.5)';
      hovered.style.outlineOffset = '2px';
    }
  }, true);

  document.addEventListener('mouseout', function(e) {
    if (dead()) return;
    if (resizing) return;
    if (hovered && hovered !== selected) {
      hovered.style.outline = '';
      hovered.style.outlineOffset = '';
    }
    hovered = null;
  }, true);

  // r123: click-through selection state. altPicked suppresses the click
  // handler that fires after mouseup, which would re-select the TOP element
  // and immediately undo the deeper pick.
  var altPicked = false;

  function selectElement(el) {
    if (selected) {
      selected.style.outline = '';
      selected.style.outlineOffset = '';
      selected.removeAttribute('data-css-selected');
    }
    removeHandles();
    selected = el;
    selected.setAttribute('data-css-selected', 'true');
    selected.style.outline = '2px solid #6366f1';
    selected.style.outlineOffset = '2px';
    createHandles(selected);
    notifyParent();
  }

  document.addEventListener('mousedown', function(e) {
    if (dead()) return;
    if (e.altKey && !(e.target.classList && e.target.classList.contains('__resize-handle'))) {
      // Alt+press: select the next element BENEATH the current selection at
      // this point. Without this, anything buried is unreachable — every
      // click lands on whatever is on top (the user's exact report: a title
      // dragged under a card could not be selected to move it back).
      e.preventDefault();
      e.stopPropagation();
      var stack = [];
      try {
        stack = document.elementsFromPoint(e.clientX, e.clientY).filter(function (el) {
          if (el === document.body || el === document.documentElement) return false;
          if (el.className && String(el.className).indexOf('__') === 0) return false;
          return true;
        });
      } catch (sErr) {}
      if (stack.length) {
        var start = 0;
        var ci = selected ? stack.indexOf(selected) : -1;
        if (ci >= 0) start = (ci + 1) % stack.length;   // one step deeper, wrapping
        selectElement(stack[start]);
        altPicked = true;
        // Arm the drag for the picked element: select-and-rescue is one
        // gesture, not select, release, press again (which would hit the top
        // element and re-arm for THAT).
        pendingDrag = { el: stack[start], x: e.clientX, y: e.clientY };
      }
      return;
    }
    if (e.target.classList && e.target.classList.contains('__resize-handle') && selected) {
      e.preventDefault();
      e.stopPropagation();
      resizing = true;
      resizeHandle = e.target;
      startX = e.clientX;
      startY = e.clientY;
      var rect = selected.getBoundingClientRect();
      startW = rect.width;
      startH = rect.height;
      // Capture position so top/left handles can anchor the opposite edge.
      var rpos = window.getComputedStyle(selected).position;
      if (rpos === 'static') selected.style.position = 'relative';
      dragStartLeft = parseInt(selected.style.left) || 0;
      dragStartTop = parseInt(selected.style.top) || 0;
    } else if (e.target !== document.body && e.target !== document.documentElement) {
      // r118: never swallow a press inside live text. The browser places the
      // caret on mousedown, so arming a drag here meant a click in a
      // contenteditable element could not put the cursor between two words —
      // fixing a single typo required retyping the whole block. Text editing
      // wins over drag arming when the press lands in editable text.
      var edHost = null;
      try { edHost = e.target.closest && e.target.closest('[contenteditable="true"]'); } catch (edErr) {}
      if (edHost) { pendingDrag = null; return; }
      // Single-gesture select + drag: arm a potential drag on press. We don't
      // select or start moving until the pointer passes a small threshold (see
      // mousemove), so a plain click still just selects without triggering a save.
      e.preventDefault();
      pendingDrag = { el: e.target, x: e.clientX, y: e.clientY };
    }
  }, true);

  document.addEventListener('mousemove', function(e) {
    if (dead()) return;
    // Promote an armed press to a drag once the pointer moves past a threshold.
    // Selects the pressed element first (unless we're already inside the current
    // selection), so press-and-drag moves any element in one gesture.
    if (pendingDrag && !dragging && !resizing) {
      var pdx = e.clientX - pendingDrag.x;
      var pdy = e.clientY - pendingDrag.y;
      if (Math.abs(pdx) > 3 || Math.abs(pdy) > 3) {
        var hit = pendingDrag.el;
        if (!(selected && (hit === selected || selected.contains(hit)))) {
          if (selected) {
            selected.style.outline = '';
            selected.style.outlineOffset = '';
            selected.removeAttribute('data-css-selected');
          }
          removeHandles();
          selected = hit;
          selected.setAttribute('data-css-selected', 'true');
          selected.style.outline = '2px solid #6366f1';
          selected.style.outlineOffset = '2px';
          createHandles(selected);
          notifyParent();
        }
        dragging = true;
        startX = pendingDrag.x;
        startY = pendingDrag.y;
        var pos2 = window.getComputedStyle(selected).position;
        if (pos2 === 'static') selected.style.position = 'relative';
        dragStartLeft = parseInt(selected.style.left) || 0;
        dragStartTop = parseInt(selected.style.top) || 0;
        // Lift above sibling columns/panels while moving — a relative-
        // positioned element otherwise slides UNDER any later-DOM sibling
        // that creates its own stacking context. Capture BOTH the inline and
        // the computed z so the drop can tell "class-styled" from "no z at all".
        dragPrevZ = selected.style.zIndex || '';
        dragPrevComputedZ = window.getComputedStyle(selected).zIndex;
        selected.style.zIndex = '9999';
        // r122: resolve the clipping container ONCE and open it for the drag.
        dragClip = clippingAncestorOf(selected);
        if (!dragClip) {
          // Not currently clipped, but it may become clipped as it moves, so
          // find the nearest clipper regardless.
          var probe = selected.parentElement;
          while (probe && probe !== document.body && probe !== document.documentElement) {
            var pcs = window.getComputedStyle(probe);
            if (pcs.overflowX !== 'visible' || pcs.overflowY !== 'visible') {
              dragClip = { el: probe, name: (probe.tagName || 'div').toLowerCase(), lostPct: 0 };
              break;
            }
            probe = probe.parentElement;
          }
        }
        revealClipsFor(selected);
        liftStackingAncestors(selected);
        selected.style.cursor = 'grabbing';
        collectSiblingRects();
        pendingDrag = null;
      }
    }
    if (resizing && resizeHandle && selected) {
      e.preventDefault();
      var dx = parseInt(resizeHandle.dataset.dx);
      var dy = parseInt(resizeHandle.dataset.dy);
      var deltaX = e.clientX - startX;
      var deltaY = e.clientY - startY;

      if (dx !== 0) {
        var newW = Math.max(20, startW + deltaX * dx);
        selected.style.width = newW + 'px';
        // West-side handles: move the left edge so the right edge stays put.
        if (dx === -1) selected.style.left = (dragStartLeft - (newW - startW)) + 'px';
      }
      if (dy !== 0) {
        var newH = Math.max(20, startH + deltaY * dy);
        selected.style.height = newH + 'px';
        // North-side handles: move the top edge so the bottom edge stays put.
        if (dy === -1) selected.style.top = (dragStartTop - (newH - startH)) + 'px';
      }
      updateHandlePositions(selected);
    } else if (dragging && selected) {
      e.preventDefault();
      var moveX = e.clientX - startX;
      var moveY = e.clientY - startY;
      selected.style.left = (dragStartLeft + moveX) + 'px';
      selected.style.top = (dragStartTop + moveY) + 'px';
      // Soft clamp: keep at least 20px of the element visible within 1280x720
      var cRect = selected.getBoundingClientRect();
      var minVis = 20;
      if (cRect.right < minVis) selected.style.left = (parseInt(selected.style.left) + (minVis - cRect.right)) + 'px';
      if (cRect.bottom < minVis) selected.style.top = (parseInt(selected.style.top) + (minVis - cRect.bottom)) + 'px';
      if (cRect.left > 1280 - minVis) selected.style.left = (parseInt(selected.style.left) - (cRect.left - (1280 - minVis))) + 'px';
      if (cRect.top > 720 - minVis) selected.style.top = (parseInt(selected.style.top) - (cRect.top - (720 - minVis))) + 'px';
      // r122: CONSTRAIN, do not report afterwards. The editor used to allow a
      // drag anywhere and then warn on release that the element was clipped,
      // off-canvas or buried. Keeping a real part of it inside its container
      // means it never reaches the state that needed a warning. A deliberate
      // move OUT of the container is an explicit action ("Lift out of
      // container"), not something you discover by accident.
      if (dragClip && dragClip.el) {
        try {
          var kr = selected.getBoundingClientRect();
          var kc = dragClip.el.getBoundingClientRect();
          // Keep at least 60% of the element inside; the remainder allows the
          // deliberate bleed a designer wants without letting it vanish.
          var keepW = kr.width * 0.6, keepH = kr.height * 0.6;
          var adjX = 0, adjY = 0;
          if (kr.right < kc.left + keepW) adjX = (kc.left + keepW) - kr.right;
          else if (kr.left > kc.right - keepW) adjX = (kc.right - keepW) - kr.left;
          if (kr.bottom < kc.top + keepH) adjY = (kc.top + keepH) - kr.bottom;
          else if (kr.top > kc.bottom - keepH) adjY = (kc.bottom - keepH) - kr.top;
          if (adjX) selected.style.left = ((parseInt(selected.style.left) || 0) + adjX) + 'px';
          if (adjY) selected.style.top = ((parseInt(selected.style.top) || 0) + adjY) + 'px';
        } catch (kErr) {}
      }
      scheduleAlignmentAndSnap();
      updateHandlePositions(selected);
    }
  }, true);

  function finishDrag() {
    // r119: flush any pending snap frame BEFORE measuring and snapshotting.
    // Otherwise the rAF lands after the snapshot and the saved position is the
    // un-snapped one — the element would visibly jump after the save.
    if (snapRaf) {
      cancelAnimationFrame(snapRaf);
      snapRaf = 0;
      checkAlignmentAndSnap();
    }
    dragging = false;
    justDragged = true;
    removeGuides();
    restoreRevealedClips();
    dragClip = null;
    restoreLiftedAncestors();
    if (selected) {
      selected.style.cursor = '';
      // The user MOVED this element — it must stay visible where dropped.
      // The old policy (restore class z / lift to 10) let anything with a
      // higher sibling z swallow it. New policy: land above the highest
      // computed z on the slide (editor artifacts and 9000+ excluded),
      // keeping an explicit inline z if it was already higher.
      var sRect = selected.getBoundingClientRect();
      var isBackdrop = sRect.width * sRect.height > 1280 * 720 * 0.7;
      if (isBackdrop) {
        // A nudged full-bleed background must NOT be lifted above the
        // slide's content — restore the pre-drag stacking.
        if (dragPrevZ !== '') selected.style.zIndex = dragPrevZ;
        else selected.style.removeProperty('z-index');
      } else {
        var maxZ = 0;
        try {
          var zAll = document.body.querySelectorAll('*');
          for (var zi = 0; zi < zAll.length && zi < 1500; zi++) {
            var zEl = zAll[zi];
            if (zEl === selected || selected.contains(zEl) ||
                (zEl.className && String(zEl.className).indexOf('__') === 0)) continue;
            var zv = parseInt(window.getComputedStyle(zEl).zIndex);
            if (!isNaN(zv) && zv > maxZ) maxZ = zv;
          }
        } catch (zErr) {}
        var prevEffective = parseInt(dragPrevZ || dragPrevComputedZ, 10) || 0;
        // Already on top -> keep the design z (repeated drags must not
        // ratchet); otherwise land just above the highest sibling. Never
        // exactly 9999: the scrub strips that value as a drag artifact.
        var landingZ = prevEffective > maxZ ? prevEffective : maxZ + 1;
        // r117: do not raise an element that was ALREADY fully visible where
        // it landed. Raising unconditionally meant each drag buried whatever
        // was dragged before it, so moving two elements in sequence hid the
        // first — the user's own report. The probe below already establishes
        // whether anything covers the drop point; when nothing does, the
        // element keeps its design stacking and the deck's paint order
        // survives editing. Bring-to-front is a button now, so the heuristic
        // no longer has to guess on the user's behalf.
        // r119: NINE points, not one. The centre-only version answered "not
        // covered" for a title whose right half was under a photo, so the
        // element kept its design z and stayed behind the image — the user's
        // "still behind it".
        // r121: DROP THE LIFT BEFORE PROBING. The element still carried the
        // drag's z-index:9999 at this point, so elementFromPoint returned the
        // element ITSELF at all nine sample points, the helper skipped them as
        // self-hits, and the answer was always "nothing covers this". So r117's
        // no-ratchet rule fired every time and every dragged element dropped
        // back to its design z — which is why "it still goes behind" got WORSE
        // after r117 rather than better, and started happening on slides where
        // it never used to. A probe cannot measure paint order while its own
        // subject is temporarily on top of everything.
        var coveredAtDrop = false;
        try {
          if (prevEffective) selected.style.zIndex = String(prevEffective);
          else selected.style.removeProperty('z-index');
          // Force a style flush so the hit test sees the restored order.
          void selected.offsetHeight;
          coveredAtDrop = coveredByForeignPaint(selected);
        } catch (pErr) { coveredAtDrop = true; }
        if (!coveredAtDrop) landingZ = prevEffective;
        if (landingZ === 9999) landingZ = 10000;
        selected.style.zIndex = String(landingZ);
        // Stacking-context escape: the element's own z cannot cross an
        // ANCESTOR's stacking context (the drag lifted ancestors temporarily,
        // the drop restored them — user report: a title dragged right still
        // vanished under a sibling column). Probe the drop point; if a
        // foreign branch covers it, persist the minimal ancestor lift.
        try {
          // r119: the ancestor-lift probe had the same centre-only blindness.
          // Reuse the shared grid test, then re-resolve one hit for the
          // ancestor walk below.
          var anyCover = coveredByForeignPaint(selected);
          var cR = selected.getBoundingClientRect();
          var probeX = Math.min(1279, Math.max(1, cR.left + cR.width * 0.92));
          var probeY = Math.min(719, Math.max(1, cR.top + cR.height / 2));
          var hit = anyCover ? document.elementFromPoint(probeX, probeY) : null;
          // A fully transparent overlay (gradient scrims default to
          // hit-testable) covers the point without hiding the element —
          // lifting for those would restack slide art on every drag.
          var hitVisible = false;
          if (hit) {
            var hcs = window.getComputedStyle(hit);
            // OWN text nodes only: textContent is subtree text, which
            // classified transparent layout wrappers as visible occluders.
            var hitOwnText = false;
            try {
              for (var tn = 0; tn < hit.childNodes.length; tn++) {
                var nd = hit.childNodes[tn];
                if (nd.nodeType === 3 && (nd.textContent || '').trim()) { hitOwnText = true; break; }
              }
            } catch (tErr) {}
            // r118: an element can PAINT without a background or text. An
            // <img>, <canvas>, <svg> or <video> was classified invisible, so
            // dropping onto a photo looked unoccluded and the element stayed
            // buried under it — the commonest case on a cover slide.
            var hitPaints = false;
            try {
              var htag = (hit.tagName || '').toUpperCase();
              hitPaints = htag === 'IMG' || htag === 'CANVAS' || htag === 'SVG' ||
                          htag === 'VIDEO' || htag === 'PICTURE' ||
                          !!hit.closest('svg');
            } catch (pkErr) {}
            if (hitPaints) hitOwnText = true;
            hitVisible = !(hcs.backgroundColor === 'rgba(0, 0, 0, 0)' &&
                           hcs.backgroundImage === 'none' &&
                           !hitOwnText);
          }
          if (hit && hitVisible && hit !== selected && !selected.contains(hit) && !hit.contains(selected) &&
              !(hit.className && String(hit.className).indexOf('__') === 0)) {
            // Persist the lift ONLY on already-positioned ancestors: adding
            // position:relative to a static (opacity/transform) ancestor
            // would create a new containing block and re-anchor every
            // absolutely-positioned descendant — moving art the user never
            // touched. Static-context traps stay unfixed (known limitation,
            // as are pointer-events:none occluders the probe cannot see).
            var anc2 = selected.parentElement;
            var liftZNum = parseInt(selected.style.zIndex, 10) || 1;
            if (liftZNum === 9999) liftZNum = 10000;
            while (anc2 && anc2 !== document.body && anc2 !== document.documentElement) {
              var acs2 = window.getComputedStyle(anc2);
              if (acs2.position !== 'static') {
                var ancZ = parseInt(acs2.zIndex, 10) || 0;
                if (ancZ < liftZNum) anc2.style.zIndex = String(liftZNum);
              }
              anc2 = anc2.parentElement;
            }
          }
        } catch (occErr) {}
      }
      createHandles(selected);
      notifyParent();
      window.parent.postMessage({ type: 'EDIT_ACTION' }, '*');
      // r117: warn when the drop has pushed most of the element off the
      // canvas. Edit mode relaxes html/body overflow so a drag is not
      // clipped mid-gesture, and the save path puts overflow:hidden back —
      // so this is content that looks placed now and disappears on save.
      // Deliberate bleeds are normal, hence "mostly off" rather than "at all
      // off".
      // r120: a CLIPPING CONTAINER, reported separately from the canvas edge.
      // This is the case the user kept describing as "behind other elements":
      // the title's own container cut it off, so no amount of stacking work
      // could ever have helped.
      try {
        var clip = clippingAncestorOf(selected);
        if (clip) {
          window.parent.postMessage({
            type: 'EDIT_CLIPPED',
            container: clip.name,
            lostPct: clip.lostPct
          }, '*');
        }
      } catch (clErr) {}
      try {
        var oR = selected.getBoundingClientRect();
        var visW = Math.max(0, Math.min(oR.right, 1280) - Math.max(oR.left, 0));
        var visH = Math.max(0, Math.min(oR.bottom, 720) - Math.max(oR.top, 0));
        var area = oR.width * oR.height;
        if (area > 0) {
          var onCanvas = (visW * visH) / area;
          if (onCanvas < 0.4) {
            window.parent.postMessage({
              type: 'EDIT_OFFCANVAS',
              onCanvasPct: Math.round(onCanvas * 100)
            }, '*');
          }
        }
      } catch (ocErr) {}
    }
  }

  document.addEventListener('mouseup', function(e) {
    if (dead()) return;
    if (resizing) {
      resizing = false;
      resizeHandle = null;
      if (selected) {
        createHandles(selected);
        notifyParent();
        window.parent.postMessage({ type: 'EDIT_ACTION' }, '*');
      }
    }
    if (dragging) {
      finishDrag();
    }
    pendingDrag = null;  // a plain click leaves an un-promoted press — clear it
  }, true);

  // Releasing the mouse OUTSIDE the iframe never fires the mouseup above,
  // which would bake the temporary z-index 9999 into the saved slide. End
  // the drag at the document boundary instead.
  document.addEventListener('mouseleave', function() {
    if (dead()) return;
    if (dragging) finishDrag();
    pendingDrag = null;
  });

  // r124: arrow-key nudge. 1px per press, 10px with Shift. Commits are
  // debounced: a run of presses is ONE history entry and ONE save, not
  // twenty — the same collapse rule the deck history applies to manual
  // sessions. The editor had no way to move an element by less than the 3px
  // drag-promotion threshold.
  var nudgeTimer = null;
  document.addEventListener('keydown', function(e) {
    if (dead()) return;
    var isArrow = e.key === 'ArrowUp' || e.key === 'ArrowDown' ||
                  e.key === 'ArrowLeft' || e.key === 'ArrowRight';
    if (isArrow && selected) {
      var ae2 = document.activeElement;
      if (ae2 && ae2.getAttribute && ae2.getAttribute('contenteditable') === 'true') return;
      e.preventDefault();
      if (window.getComputedStyle(selected).position === 'static') {
        selected.style.position = 'relative';
      }
      var step = e.shiftKey ? 10 : 1;
      var dxN = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      var dyN = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
      if (dxN) selected.style.left = ((parseInt(selected.style.left) || 0) + dxN) + 'px';
      if (dyN) selected.style.top = ((parseInt(selected.style.top) || 0) + dyN) + 'px';
      updateHandlePositions(selected);
      if (nudgeTimer) clearTimeout(nudgeTimer);
      nudgeTimer = setTimeout(function () {
        nudgeTimer = null;
        notifyParent();
        window.parent.postMessage({ type: 'EDIT_ACTION' }, '*');
      }, 500);
      return;
    }
    if (e.key !== 'Escape') return;
    // Text editing owns Escape while a contenteditable is focused (it blurs).
    var ae = document.activeElement;
    if (ae && ae.getAttribute && ae.getAttribute('contenteditable') === 'true') return;
    if (selected) {
      selected.style.outline = '';
      selected.style.outlineOffset = '';
      selected.removeAttribute('data-css-selected');
      selected = null;
      removeHandles();
      window.parent.postMessage({ type: 'ELEMENT_SELECTED', element: null }, '*');
    }
  });

  document.addEventListener('click', function(e) {
    if (dead()) return;
    if (e.target.classList && e.target.classList.contains('__resize-handle')) return;
    if (altPicked) { altPicked = false; e.preventDefault(); e.stopPropagation(); return; }
    if (justDragged) { justDragged = false; return; }
    e.preventDefault();
    e.stopPropagation();

    if (selected) {
      selected.style.outline = '';
      selected.style.outlineOffset = '';
      selected.removeAttribute('data-css-selected');
    }
    removeHandles();

    selected = e.target;
    if (selected === document.body || selected === document.documentElement) {
      selected = null;
      window.parent.postMessage({ type: 'ELEMENT_SELECTED', element: null }, '*');
      return;
    }

    selected.setAttribute('data-css-selected', 'true');
    selected.style.outline = '2px solid #6366f1';
    selected.style.outlineOffset = '2px';
    createHandles(selected);
    notifyParent();
  }, true);
})();
`;

const HtmlSlideEditor = forwardRef(function HtmlSlideEditor({ slide, slideIndex, onSlideUpdate, onElementSelected, onFactCheck, onDriveFactCheck, onRegionSelect, onQaSlide, onSlideVariation, onVariationKept, canUndo, onUndo, canRedo, onRedo, onDelete, totalSlides, readOnly, externalEditMode }, ref) {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [editMode, setEditMode] = useState('view'); // 'view' | 'text' | 'style'
  const [factChecking, setFactChecking] = useState(false);
  const [qaRunning, setQaRunning] = useState(false);
  // Variation state — when a variation is generated and pending review,
  // we show it inline with Keep / Discard. The original is retained in
  // pendingVariation.original so Discard can snap back.
  const [variationRunning, setVariationRunning] = useState(false);
  const [pendingVariation, setPendingVariation] = useState(null);  // { html, preview_url, original: {html, preview_url} }
  // Slider position for the variation compare view (0-100, % from left).
  // When pendingVariation is active, the original shows on the left side
  // of the divider and the new variation shows on the right.
  const [compareSlider, setCompareSlider] = useState(50);
  const compareIframeRef = useRef(null);
  const compareDraggingRef = useRef(false);
  const [scale, setScale] = useState(1);
  const editHistoryRef = useRef([]); // Stack of HTML snapshots
  const historyIndexRef = useRef(-1);
  const [historyLen, setHistoryLen] = useState(0); // trigger re-render for button state
  const [historyIdx, setHistoryIdx] = useState(-1);

  // Format toolbar state — the selected element's computed styles (from the
  // picker's ELEMENT_SELECTED payload) + the fonts actually loaded in this
  // slide (arbitrary families would silently substitute; only present ones
  // render true).
  const [selectedElInfo, setSelectedElInfo] = useState(null);
  // HTML we last serialized OURSELVES (autosave / Done). When it round-trips
  // through the parent and back as a prop, rewriting the iframe for it would
  // tear down the live editing session (listeners, selection, the format
  // toolbar) ~1.3s after every edit. Only external changes rewrite.
  const lastSerializedRef = useRef(null);

  // Region selection state
  const [regionMode, setRegionMode] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  // Expose applyStyleChange to parent via ref
  useImperativeHandle(ref, () => ({
    applyStyleChange(property, value) {
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (!doc) return;
      const selected = doc.querySelector('[data-css-selected="true"]');
      if (!selected) return;
      const camel = property.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      selected.style[camel] = value;
      // Debounced commit. r124 added the save; r125 makes a whole
      // property-exploration ONE undo step: a colour picker emits a settled
      // change at every pause, and each one pushed its own history entry, so
      // undoing one background decision took a dozen presses. Consecutive
      // commits for the SAME property replace the previous entry — guarded by
      // comparing the top of history to the entry we wrote, so any OTHER edit
      // in between (a drag, a nudge) ends the run and is never overwritten.
      // Refs, not `this`: the imperative handle is recreated every render, so
      // state stored on it evaporates mid-burst.
      if (styleCommitRef.current.timer) clearTimeout(styleCommitRef.current.timer);
      styleCommitRef.current.timer = setTimeout(() => {
        const sc = styleCommitRef.current;
        const hist = editHistoryRef.current;
        const idx = historyIndexRef.current;
        const replaceTop = sc.prop === property
          && idx === hist.length - 1
          && hist[idx] === sc.html;
        pushSnapshot({ replaceTop });
        sc.prop = property;
        sc.html = editHistoryRef.current[historyIndexRef.current];
        autoSave();
      }, 500);
    },
    saveAndSerialize() {
      return serializeHtml();
    },
    getRegion() {
      return selectedRegion;
    },
    clearRegion() {
      setSelectedRegion(null);
      setRegionMode(false);
      onRegionSelect?.(null);
    },
  }));

  // Compute scale to fit 1280x720 iframe into container
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setScale(containerWidth / 1280);
      }
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Write HTML to iframe when slide changes
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !slide?.html_source) return;

    // Our own serialization echoing back — the iframe already shows exactly
    // this state; a rewrite would only destroy the live session.
    if (slide.html_source === lastSerializedRef.current) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    // A genuine external rewrite kills any live selection.
    setSelectedElInfo(null);

    // Wrap inline scripts in IIFE to prevent "already declared" errors on re-render
    const safeHtml = slide.html_source.replace(
      /<script(?![^>]*\bsrc\b)([^>]*)>([\s\S]*?)<\/script>/gi,
      (_, attrs, body) => body.trim() ? `<script${attrs}>(function(){${body}})()</script>` : `<script${attrs}></script>`
    );
    // Inject overflow constraint so content stays within 1280x720
    const overflowStyle = '<style>html,body{margin:0;padding:0;width:1280px;height:720px;overflow:hidden;}</style>';
    doc.open();
    doc.write(overflowStyle + safeHtml);
    doc.close();

    // After doc.close(), external CDN scripts (e.g. Chart.js) may not have loaded
    // before inline scripts executed, leaving canvases blank. Once the iframe
    // finishes loading all sub-resources, check if charts actually rendered —
    // only re-run inline scripts if canvases exist but have no Chart instance.
    const reinitCharts = () => {
      try {
        const iDoc = iframe.contentDocument;
        const iWin = iframe.contentWindow;
        if (!iDoc || !iWin) return;
        const canvases = iDoc.querySelectorAll('canvas');
        if (canvases.length === 0) return;
        const ChartClass = iWin.Chart;
        // If Chart.js loaded and every canvas already has an instance, skip —
        // re-executing would throw "Canvas is already in use".
        if (ChartClass?.getChart) {
          const allRendered = Array.from(canvases).every(c => ChartClass.getChart(c));
          if (allRendered) return;
          // Destroy partial instances before re-init
          canvases.forEach(c => { try { ChartClass.getChart(c)?.destroy(); } catch (_) {} });
        }
        // Re-execute every inline <script> (CDN tags with src are skipped)
        const inlineScripts = iDoc.querySelectorAll('script:not([src])');
        inlineScripts.forEach(old => {
          const fresh = iDoc.createElement('script');
          fresh.textContent = old.textContent;
          old.parentNode.replaceChild(fresh, old);
        });
      } catch (_) { /* cross-origin or destroyed doc */ }
    };
    iframe.addEventListener('load', reinitCharts, { once: true });

    // Re-inject mode after rewrite
    if (editMode === 'text') {
      setTimeout(() => enableEditing(doc, false), 500);
    } else if (editMode === 'combined') {
      setTimeout(() => { enableEditing(doc, true); enableStylePicker(doc); }, 500);
    } else if (editMode === 'style') {
      setTimeout(() => enableStylePicker(doc), 500);
    }
  }, [slide?.html_source, slideIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset selection when slide changes
  useEffect(() => {
    if (onElementSelected) onElementSelected(null);
    setSelectedElInfo(null);
  }, [slideIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for postMessage from iframe (element selection in style mode)
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'ELEMENT_SELECTED' && event.source === iframeRef.current?.contentWindow) {
        // r126: the floating format toolbar is retired — the docked Style
        // panel is the one styling surface. The panel needs the fonts
        // ACTUALLY LOADED in this slide's document (a global font list would
        // render fallbacks and lie), so they ride along on the payload.
        setSelectedElInfo(event.data.element || null);
        if (onElementSelected) {
          onElementSelected(event.data.element
            ? { ...event.data.element, availableFonts: collectDocFonts() }
            : event.data.element);
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onElementSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const enableEditing = useCallback((doc, dblClickOnly = false) => {
    if (!doc?.body) return;

    const style = doc.createElement('style');
    style.id = 'html-editor-styles';
    style.textContent = `
      [contenteditable="true"] { cursor: text; transition: outline 0.15s; }
      [contenteditable="true"]:hover { outline: 1px dashed rgba(59,130,246,0.4); outline-offset: 2px; }
      [contenteditable="true"]:focus { outline: 2px solid #3b82f6; outline-offset: 2px; }
      [data-text-editable]:hover { outline: 1px dashed rgba(59,130,246,0.3); outline-offset: 2px; }
    `;
    doc.head.appendChild(style);

    const selectors = 'h1, h2, h3, h4, h5, h6, p, span, li, td, th, label, a';
    const elements = doc.querySelectorAll(selectors);
    elements.forEach(el => {
      if (el.textContent.trim().length > 0) {
        if (dblClickOnly) {
          // In combined mode: double-click to enter text editing, single-click for select/drag
          el.setAttribute('data-text-editable', 'true');
          el.addEventListener('dblclick', function handler(e) {
            // Same kill switch as the picker script: these per-element
            // listeners survive Done too.
            if (doc.defaultView?.__editorDisabled) return;
            e.stopPropagation();
            el.contentEditable = 'true';
            el.focus();
            // Select all text in element for easy replacement
            const range = doc.createRange();
            range.selectNodeContents(el);
            const sel = doc.defaultView.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          });
          el.addEventListener('blur', () => {
            el.contentEditable = 'false';
            window.parent.postMessage({ type: 'EDIT_ACTION' }, '*');
          });
          el.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); el.blur(); }
            else if (e.key === 'Escape') { el.blur(); }
          });
        } else {
          el.contentEditable = 'true';
          el.addEventListener('blur', () => {
            window.parent.postMessage({ type: 'EDIT_ACTION' }, '*');
          });
          el.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); el.blur(); }
            else if (e.key === 'Escape') { el.blur(); }
          });
        }
      }
    });
  }, []);

  const enableStylePicker = useCallback((doc) => {
    if (!doc?.body) return;

    // Idempotent re-inject. After an autosave/undo, slide.html_source changes and
    // the iframe is rewritten via doc.open()/write()/close() — which wipes the
    // document's event listeners but NOT window.__stylePickerActive, since the
    // Window survives the rewrite. Left stale, the re-injected picker script hits
    // `if (window.__stylePickerActive) return;` and bails WITHOUT rebinding the
    // drag/resize listeners, so dragging dies after the first move while text
    // editing (ungated) keeps working. Reset the flag and drop any stale picker
    // script/style (which also otherwise accumulate in the serialized HTML) so the
    // fresh injection always rebinds.
    if (doc.defaultView) doc.defaultView.__stylePickerActive = false;
    doc.querySelectorAll('#css-picker-script, #css-picker-styles').forEach(el => el.remove());

    // Add hover/selection styles
    const style = doc.createElement('style');
    style.id = 'css-picker-styles';
    style.textContent = `
      body { cursor: crosshair !important; }
      body * { cursor: crosshair !important; }
      [data-css-selected="true"] { cursor: grab !important; }
      [data-css-selected="true"] * { cursor: grab !important; }
    `;
    doc.head.appendChild(style);

    // Inject the picker script
    const script = doc.createElement('script');
    script.id = 'css-picker-script';
    script.textContent = STYLE_PICKER_SCRIPT;
    doc.body.appendChild(script);
  }, []);

  // Editor artifacts that must NEVER persist into html_source. Works on a
  // live document root or a DETACHED CLONE (autoSave serializes a scrubbed
  // clone so the on-screen selection outline/handles stay visible while the
  // persisted HTML is clean).
  const scrubEditorArtifacts = useCallback((root) => {
    if (!root) return;
    // Editing styles + picker script/styles (duplicate ids all removed)
    root.querySelectorAll('#html-editor-styles, #css-picker-styles, #css-picker-script').forEach(el => el.remove());
    // Both contenteditable states: blur leaves "false" behind, and neither
    // belongs in saved slide HTML. data-text-editable is the combined-mode
    // dblclick marker — same story.
    root.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
    root.querySelectorAll('[data-text-editable]').forEach(el => el.removeAttribute('data-text-editable'));
    root.querySelectorAll('[data-css-selected]').forEach(el => el.removeAttribute('data-css-selected'));
    // Scrub editor-signature outlines from EVERY element — hover outlines
    // (1px dashed indigo) and selection outlines otherwise become slide
    // content. Test outlineColor itself (browser-normalized rgb form), NOT
    // the whole style attribute: slides legitimately use #6366f1 elsewhere.
    root.querySelectorAll('[style*="outline"]').forEach(el => {
      const oc = el.style.outlineColor || '';
      if (oc === 'rgb(99, 102, 241)' || oc.startsWith('rgba(99, 102, 241')) {
        el.style.outline = '';
        el.style.outlineOffset = '';
      }
    });
    // Transient drag styles that must never persist
    root.querySelectorAll('[style*="cursor"]').forEach(el => {
      if (el.style.cursor === 'grabbing' || el.style.cursor === 'grab') el.style.cursor = '';
    });
    root.querySelectorAll('[style*="9999"]').forEach(el => {
      if (el.style.zIndex === '9999') el.style.removeProperty('z-index');
    });
    // Resize handles and guide lines
    root.querySelectorAll('.__resize-handle, .__guide-line, .__clip-hints, #__clip-hints').forEach(el => el.remove());
  }, []);

  const cleanupMode = useCallback((doc) => {
    if (!doc) return;
    scrubEditorArtifacts(doc.documentElement || doc);
    // Reset the picker flag so it can be re-injected — and KILL the
    // still-bound document listeners (removing the <script> element does
    // not unbind them; without this flag the slide stays selectable and
    // draggable after Done).
    if (doc.defaultView) {
      doc.defaultView.__stylePickerActive = false;
      doc.defaultView.__editorDisabled = true;
    }
  }, [scrubEditorArtifacts]);

  const serializeHtml = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return null;

    // Clean all artifacts before serializing
    cleanupMode(doc);
    return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
  }, [cleanupMode]);

  // --- Undo/Redo helpers ---
  // r125: style-panel burst state. On the imperative handle (`this`) it would
  // reset every render, because useImperativeHandle recreates the handle.
  const styleCommitRef = useRef({ timer: null, prop: null, html: null });

  const pushSnapshot = useCallback((opts = {}) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    // r117: snapshot from a CLONE. This used to strip the eight resize
    // handles, the guide lines and the selection outline off the LIVE
    // document, serialise, then put them all back — on every drag-end. That
    // is exactly the DOM thrash the "cheap reposition" optimisation above
    // exists to avoid, reintroduced at the gesture boundary, and it is the
    // main reason dropping an element felt sticky. It was also fragile: an
    // exception mid-snapshot left the editor with no handles.
    //
    // scrubEditorArtifacts already removes every artifact this did by hand
    // (handles, guides, selection marker, editor outlines, grab cursors, the
    // 9999 drag lift) and it is the same function autoSave uses, so history
    // entries and saved HTML now agree by construction instead of by two
    // parallel implementations that could drift.
    const clone = doc.documentElement.cloneNode(true);
    scrubEditorArtifacts(clone);
    const html = `<!DOCTYPE html>\n${clone.outerHTML}`;
    // Identical to the top of history means nothing actually changed (a click
    // that selected without moving, a drag that returned to origin). Pushing
    // it would cost a serialise and make Ctrl+Z a no-op the user has to press
    // twice.
    const histNow = editHistoryRef.current;
    const idxNow = historyIndexRef.current;
    if (histNow[idxNow] === html) return;
    // r125: replace-in-place for same-property bursts (see applyStyleChange).
    // Only ever replaces the TOP of history, so undo depth below it is intact.
    if (opts.replaceTop && idxNow >= 0 && idxNow === histNow.length - 1) {
      histNow[idxNow] = html;
      return;
    }
    const idx = historyIndexRef.current;
    const trimmed = editHistoryRef.current.slice(0, idx + 1);
    trimmed.push(html);
    editHistoryRef.current = trimmed;
    historyIndexRef.current = trimmed.length - 1;
    setHistoryLen(trimmed.length);
    setHistoryIdx(trimmed.length - 1);
  }, [scrubEditorArtifacts, ]);

  const restoreSnapshot = useCallback((html, reinject = true) => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc || !html) return;
    const safeHtml = html.replace(
      /<script(?![^>]*\bsrc\b)([^>]*)>([\s\S]*?)<\/script>/gi,
      (_, attrs, body) => body.trim() ? `<script${attrs}>(function(){${body}})()</script>` : `<script${attrs}></script>`
    );
    const overflowStyle = '<style>html,body{margin:0;padding:0;width:1280px;height:720px;overflow:hidden;}</style>';
    doc.open();
    doc.write(overflowStyle + safeHtml);
    doc.close();
    // The rewrite killed the live selection — the toolbar must not keep
    // acting on a stale element description.
    setSelectedElInfo(null);
    if (onElementSelected) onElementSelected(null);
    // Re-inject current mode. reinject=false (the Cancel path): the target
    // state is VIEW — re-injecting for the closure's still-'combined'
    // editMode left the slide live and draggable after "Cancel all edits",
    // with accidental drags autosaving edits the user had just discarded.
    setTimeout(() => {
      if (!reinject) {
        if (doc.defaultView) doc.defaultView.__editorDisabled = true;
        if (doc.body) doc.body.style.overflow = 'hidden';
        if (doc.documentElement) doc.documentElement.style.overflow = 'hidden';
        return;
      }
      if (editMode === 'text') enableEditing(doc, false);
      else if (editMode === 'combined') { enableEditing(doc, true); enableStylePicker(doc); }
      else if (editMode === 'style') enableStylePicker(doc);
      // Allow overflow during edit mode so dragged elements aren't clipped
      if (editMode !== 'view') {
        // r122: NO global overflow relaxation. This made the canvas
        // scrollable for the whole session (a scrollbar under the slide, and
        // content sliding sideways under the pointer). Clipping containers are
        // now opened only for the element being dragged, by revealClipsFor.
        if (doc.body) doc.body.style.overflow = 'hidden';
        if (doc.documentElement) doc.documentElement.style.overflow = 'hidden';
        // r125: the static clip hints that lived here drew dashed rectangles
        // around EVERY clipping container for the whole session — the user
        // read them as spurious borders ("borders even when there is no
        // table"). The hints now draw inside the iframe script, only while a
        // drag is active, and only for the dragged element's own containers.
      }
    }, 100);
  }, [editMode, enableEditing, enableStylePicker, onElementSelected]);

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    setHistoryIdx(historyIndexRef.current);
    restoreSnapshot(editHistoryRef.current[historyIndexRef.current]);
  }, [restoreSnapshot]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= editHistoryRef.current.length - 1) return;
    historyIndexRef.current += 1;
    setHistoryIdx(historyIndexRef.current);
    restoreSnapshot(editHistoryRef.current[historyIndexRef.current]);
  }, [restoreSnapshot]);

  const handleCancel = useCallback(() => {
    if (editHistoryRef.current.length > 0) {
      // reinject=false: the target state is view mode — the editor must be
      // fully disarmed, not re-armed for the closure's stale editMode.
      restoreSnapshot(editHistoryRef.current[0], false);
    } else {
      // No history (nothing edited) — still disarm the injected listeners.
      const doc = iframeRef.current?.contentDocument;
      if (doc) cleanupMode(doc);
      if (doc?.body) doc.body.style.overflow = 'hidden';
      if (doc?.documentElement) doc.documentElement.style.overflow = 'hidden';
    }
    editHistoryRef.current = [];
    historyIndexRef.current = -1;
    setHistoryLen(0);
    setHistoryIdx(-1);
    setEditMode('view');
    setSelectedElInfo(null);
    if (onElementSelected) onElementSelected(null);
    // r144b P2: under DECK control, dropping to view stranded the slide —
    // the per-slide Edit button is hidden and the external effect only
    // re-runs on the deck flag. Re-enter once the reverted doc settles.
    if (externalEditMode) {
      setTimeout(() => {
        try {
          const d = iframeRef.current?.contentDocument;
          if (d && d.body && editModeRef.current === 'view') handleModeSwitchRef.current?.('combined');
        } catch { /* noop */ }
      }, 250);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreSnapshot, cleanupMode, onElementSelected, externalEditMode]);

  // ── Format toolbar helpers ──
  const collectDocFonts = () => {
    // Families actually rendering in this slide (first family of each
    // computed stack), plus web-safe staples — a font not loaded in the
    // document would silently substitute if offered.
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc?.body) return [];
      const seen = new Set();
      const els = doc.body.querySelectorAll('*');
      for (let i = 0; i < els.length && i < 800; i++) {
        const ff = (doc.defaultView.getComputedStyle(els[i]).fontFamily || '')
          .split(',')[0].trim().replace(/['"]/g, '');
        if (ff && !ff.startsWith('__')) seen.add(ff);
      }
      const WEB_SAFE = ['Arial', 'Georgia', 'Times New Roman', 'Courier New'];
      // Ground-truth filter: a family whose webfont failed to load would
      // silently substitute — drop it unless document.fonts confirms it.
      let list = [...seen];
      try {
        const dFonts = doc.fonts;
        if (dFonts?.check) {
          list = list.filter(f => WEB_SAFE.includes(f) || dFonts.check(`12px "${f}"`));
        }
      } catch { /* keep unfiltered */ }
      WEB_SAFE.forEach(f => { if (!list.includes(f)) list.push(f); });
      return list.sort((a, b) => a.localeCompare(b));
    } catch { return []; }
  };

  // r126: rgbToHex / isTransparentColor left with the toolbar; the panel
  // carries its own gradient-text guard now.

  // Auto-save: persist HTML back to parent after every edit action.
  // CRITICAL: serialize a SCRUBBED CLONE, never the live document — the live
  // DOM carries the selection outline/contenteditable/picker script the user
  // is actively using, and serializing it raw bakes those boxes into
  // html_source (visible in thumbnails and after reopening the deck).
  const autoSaveTimerRef = useRef(null);
  const autoSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (!doc) return;
      const clone = doc.documentElement.cloneNode(true);
      scrubEditorArtifacts(clone);
      // Edit mode relaxes overflow so drags aren't clipped; persisted HTML
      // must keep the slide clipped like the mode-exit path does.
      clone.style.overflow = 'hidden';
      const cloneBody = clone.querySelector('body');
      if (cloneBody) cloneBody.style.overflow = 'hidden';
      const updatedHtml = `<!DOCTYPE html>\n${clone.outerHTML}`;
      lastSerializedRef.current = updatedHtml;
      onSlideUpdate(slideIndex, { ...slide, html_source: updatedHtml, _needsRescreenshot: true });
    }, 800);
  }, [onSlideUpdate, slideIndex, slide, scrubEditorArtifacts]);

  // Apply a style from the format toolbar to the selected element: instant
  // visual feedback, debounced history snapshot + autosave (a color-picker
  // drag fires dozens of input events).
  // r126: the inline-style helper was removed with the floating toolbar it
  // served — the docked panel styles through applyStyleChange on the ref.

  // Delete selected element with Delete/Backspace key in style mode
  /**
   * r117: explicit stacking order.
   *
   * The drop heuristic guesses a z-index (land just above the highest sibling)
   * and there was NO way to overrule it. When it guessed wrong the user was
   * stuck, and because every drop landed above everything, dragging a second
   * element buried the first — reported as "dragging elements can cause them
   * to hide behind other elements".
   *
   * `front` walks the document for the highest z and goes one above it.
   * `back` goes one below the lowest, floored at 0 so an element cannot slip
   * behind the slide's own background layer. Only positioned elements honour
   * z-index at all, so a static element is promoted to relative first, the
   * same promotion the drag already performs.
   */
  /**
   * r122: LIFT OUT OF CONTAINER — the cure the clip warning never had.
   *
   * r120 could finally name the container cutting an element off, but naming it
   * is a diagnosis with no remedy: the user's only options were drag it back
   * inside or undo. This moves the element to the slide root and pins it where
   * it visually sits, so it stops being clipped by anything. Explicit, because
   * leaving a container is a real design decision and must never happen by
   * accident during a drag.
   */
  const handleLiftOut = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    const selected = doc.querySelector('[data-css-selected="true"]');
    if (!selected || selected === doc.body || selected === doc.documentElement) return;
    if (selected.parentElement === doc.body) {
      toast.info('That element is already at the slide root, so nothing is clipping it.');
      return;
    }
    try {
      // Capture the position BEFORE reparenting: the whole point is that the
      // element does not appear to move.
      const r = selected.getBoundingClientRect();
      const win = doc.defaultView;
      const cs = win.getComputedStyle(selected);
      // r123: if the element is IN FLOW, removing it collapses the space it
      // occupies and every following sibling reflows upward — the user lifted
      // a title out and watched the subtitle "randomly" jump to the top of the
      // panel and get clipped itself. Leave an invisible placeholder with the
      // same box, so the design's spacing survives the lift. Absolute/fixed
      // elements take no space, so they need no placeholder.
      const wasInFlow = cs.position === 'static' || cs.position === 'relative';
      if (wasInFlow && selected.parentElement) {
        const ph = doc.createElement('div');
        ph.setAttribute('data-wiz-liftout-placeholder', '1');
        ph.style.cssText =
          `width:${Math.round(r.width)}px;height:${Math.round(r.height)}px;` +
          `margin:${cs.margin};visibility:hidden;pointer-events:none;`;
        selected.parentElement.insertBefore(ph, selected);
      }
      selected.style.position = 'absolute';
      selected.style.left = `${Math.round(r.left)}px`;
      selected.style.top = `${Math.round(r.top)}px`;
      // Width must be pinned too: out of its flex/grid parent the element would
      // otherwise collapse to its content or stretch to the body.
      if (cs.width && cs.width !== 'auto') selected.style.width = `${Math.round(r.width)}px`;
      selected.style.margin = '0';
      doc.body.appendChild(selected);
      // Land above the deck's own art, since the reason for lifting out is that
      // something was covering or cutting it.
      let maxZ = 0;
      const all = doc.body.querySelectorAll('*');
      for (let i = 0; i < all.length && i < 1500; i++) {
        const el = all[i];
        if (el === selected || selected.contains(el)) continue;
        if (el.className && String(el.className).indexOf('__') === 0) continue;
        const z = parseInt(win.getComputedStyle(el).zIndex, 10);
        if (!isNaN(z) && z > maxZ && z < 9000) maxZ = z;
      }
      selected.style.zIndex = String(maxZ + 1);
      pushSnapshot();
      autoSave();
      toast.success('Lifted out of its container. It is no longer clipped, and it '
        + 'keeps its position.', { id: 'lift-out', duration: 4000 });
    } catch (e) {
      toast.error('Could not lift that element out.');
    }
  }, [pushSnapshot, autoSave]);

  const handleRestack = useCallback((dir) => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    const selected = doc.querySelector('[data-css-selected="true"]');
    if (!selected || selected === doc.body || selected === doc.documentElement) return;
    const win = doc.defaultView;
    if (win.getComputedStyle(selected).position === 'static') {
      selected.style.position = 'relative';
    }
    let maxZ = 0;
    let minZ = 0;
    const all = doc.body.querySelectorAll('*');
    for (let i = 0; i < all.length && i < 1500; i++) {
      const el = all[i];
      if (el === selected || selected.contains(el)) continue;
      if (el.className && String(el.className).indexOf('__') === 0) continue;
      const z = parseInt(win.getComputedStyle(el).zIndex, 10);
      if (!isNaN(z)) {
        if (z > maxZ) maxZ = z;
        if (z < minZ) minZ = z;
      }
    }
    // 9999 is the transient drag lift and is scrubbed on save, so landing on
    // it would silently drop the user's choice.
    let target = dir === 'front' ? maxZ + 1 : Math.max(0, minZ - 1);
    if (target === 9999) target = 10000;
    selected.style.zIndex = String(target);
    pushSnapshot();
    autoSave();
  }, [pushSnapshot, autoSave]);

  const handleDeleteSelected = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    const selected = doc.querySelector('[data-css-selected="true"]');
    if (!selected) return;
    // Don't delete body or root containers
    if (selected === doc.body || selected === doc.documentElement) return;
    // r118: Delete removed ANYTHING with no confirmation, and selection is
    // whatever was clicked — a card's padding, a grid gap or the full-bleed
    // background div all select the CONTAINER, not the text inside it. So
    // clicking "empty space" to deselect (there is no Escape-to-deselect) and
    // pressing Delete could take out the slide's whole background or content
    // column, autosaved, with the in-editor stack wiped on Done. Deleting a
    // whole SLIDE asks for confirmation; deleting the entire contents of one
    // did not. Measured, not guessed by tag name.
    try {
      const r = selected.getBoundingClientRect();
      const coversMost = (r.width * r.height) > (1280 * 720 * 0.25);
      let textCount = 0;
      selected.querySelectorAll('*').forEach((el) => {
        for (let i = 0; i < el.childNodes.length; i++) {
          const n = el.childNodes[i];
          if (n.nodeType === 3 && (n.textContent || '').trim()) { textCount += 1; break; }
        }
      });
      if (coversMost || textCount >= 3) {
        const what = (selected.tagName || 'element').toLowerCase()
          + (selected.className ? ' .' + String(selected.className).split(' ')[0] : '');
        const pct = Math.round((r.width * r.height) / (1280 * 720) * 100);
        const ok = window.confirm(
          `Delete <${what}>?\n\nIt covers about ${pct}% of the slide and contains `
          + `${textCount} text block${textCount === 1 ? '' : 's'}. `
          + 'This removes all of it.'
        );
        if (!ok) return;
      }
    } catch (dErr) { /* measurement failure must not block a normal delete */ }
    selected.remove();
    // Remove handles
    doc.querySelectorAll('.__resize-handle').forEach(h => h.remove());
    if (onElementSelected) onElementSelected(null);
    pushSnapshot();
    autoSave();
  }, [onElementSelected, pushSnapshot, autoSave]);

  // Keyboard shortcuts for undo/redo/delete
  useEffect(() => {
    if (editMode === 'view') return;
    // r144b P1-4: in DECK edit mode every slide registers this window
    // handler — one Ctrl+Z or Delete would hit ALL slides (including the
    // off-screen ones, invisibly). Only the instance owning the user's
    // focus responds.
    const _keyGuard = (e) => {
      if (externalEditMode === undefined) return true;
      const ae = document.activeElement;
      return !!(containerRef.current && ae && containerRef.current.contains(ae));
    };
    const handler = (e) => {
      if (!_keyGuard(e)) return; // deck mode: focus ownership gates EVERY shortcut
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === ']' || e.key === '[')) {
        // r117: the conventional stacking shortcuts, so the buttons are not
        // the only route.
        e.preventDefault();
        handleRestack(e.key === ']' ? 'front' : 'back');
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && (editMode === 'style' || editMode === 'combined')) {
        // Only delete if not focused on a text input
        const activeEl = document.activeElement;
        if (activeEl?.tagName !== 'INPUT' && activeEl?.tagName !== 'TEXTAREA') {
          e.preventDefault();
          handleDeleteSelected();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editMode, handleUndo, handleRedo, handleDeleteSelected, handleRestack]);

  // Listen for EDIT_ACTION from iframe (drag/resize end) to push snapshot + auto-save
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'EDIT_ACTION' && event.source === iframeRef.current?.contentWindow) {
        pushSnapshot();
        autoSave();
      }
      // r117: the clipping trap, surfaced. Without this the user only finds
      // out after saving, when the element they carefully placed is gone.
      // r120: a clip is not a stacking problem, so say what it actually is.
      // Bring-to-front cannot fix it and the user would otherwise keep
      // reaching for that button.
      if (event.data?.type === 'EDIT_CLIPPED' && event.source === iframeRef.current?.contentWindow) {
        toast.warning(
          `${event.data.lostPct}% of that element is cut off by its container `
          + `(<${event.data.container}>). This is clipping, not stacking, so `
          + 'Bring to front will not help. Use Lift out of container, or move '
          + 'it back inside.',
          { id: 'edit-clipped', duration: 7000 }
        );
      }
      if (event.data?.type === 'EDIT_OFFCANVAS' && event.source === iframeRef.current?.contentWindow) {
        toast.warning(
          `That element is now ${100 - (event.data.onCanvasPct || 0)}% outside the slide. `
          + 'The editor shows overflow, but the saved slide is cropped to 1280x720, '
          + 'so this part will be cut. Move it back inside, or press Ctrl+Z.',
          { id: 'edit-offcanvas', duration: 6000 }
        );
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [pushSnapshot, autoSave]);

  useEffect(() => { handleModeSwitchRef.current = handleModeSwitch; });

  // r144: DECK-LEVEL edit mode — the header's single "Edit mode" button
  // drives every slide's editor at once (per-slide Edit buttons hide when
  // this prop is provided). Entering/leaving reuses handleModeSwitch (the
  // const below — the effect callback runs post-render, so no TDZ), and a
  // not-yet-loaded iframe is retried briefly.
  const editModeRef = useRef(editMode);
  useEffect(() => { editModeRef.current = editMode; }, [editMode]);
  const externalTriesRef = useRef(0);
  useEffect(() => {
    if (externalEditMode === undefined) return undefined;
    externalTriesRef.current = 0;
    let timer = null;
    const attempt = () => {
      const wantOn = !!externalEditMode && !readOnly;
      if (wantOn === (editModeRef.current !== 'view')) return; // already there
      const doc = iframeRef.current?.contentDocument;
      if (!doc || !doc.body) {
        if (externalTriesRef.current++ < 20) timer = setTimeout(attempt, 150);
        return;
      }
      if (wantOn) handleModeSwitch('combined');
      else handleModeSwitch(editModeRef.current); // same-mode switch = save & exit
    };
    attempt();
    return () => { if (timer) clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalEditMode, readOnly]);

  const handleModeSwitchRef = useRef(null);
  const handleModeSwitch = (newMode) => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    // If leaving any edit mode, save changes
    if (editMode !== 'view' && (newMode === editMode || newMode === 'combined')) {
      // r144b P1-3: an UNTOUCHED slide must not be re-serialized — a deck
      // toggle exits every slide at once, and unconditional saves fired N
      // rescreenshots (headless renders) + N history entries per toggle.
      // pushSnapshot fires on every real edit, so length 1 = untouched.
      const _touched = editHistoryRef.current.length > 1 || historyIndexRef.current > 0;
      cleanupMode(doc);
      // Restore overflow hidden for final render
      if (doc.body) doc.body.style.overflow = 'hidden';
      if (doc.documentElement) doc.documentElement.style.overflow = 'hidden';
      if (_touched) {
        const updatedHtml = `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
        lastSerializedRef.current = updatedHtml;
        onSlideUpdate(slideIndex, { ...slide, html_source: updatedHtml, _needsRescreenshot: true });
      }
      if (onElementSelected) onElementSelected(null);
      setSelectedElInfo(null);
      // Toggle off — go back to view mode
      editHistoryRef.current = [];
      historyIndexRef.current = -1;
      setHistoryLen(0);
      setHistoryIdx(-1);
      setEditMode('view');
      return;
    }

    // Enter new mode — capture initial snapshot for undo
    const initialHtml = `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
    editHistoryRef.current = [initialHtml];
    historyIndexRef.current = 0;
    setHistoryLen(1);
    setHistoryIdx(0);

    // r122: the SECOND site that relaxed overflow for the whole session — the
    // mode-switch path. Fixing only the re-inject path left the scrollbar in
    // place for anyone entering edit mode by switching modes, which is the
    // normal route. The canvas stays 1280x720; clipping containers are opened
    // per-drag by revealClipsFor instead.
    if (doc.body) doc.body.style.overflow = 'hidden';
    if (doc.documentElement) doc.documentElement.style.overflow = 'hidden';

    if (newMode === 'text') {
      enableEditing(doc, false);
    } else if (newMode === 'combined') {
      enableEditing(doc, true); // Double-click for text, single-click for drag/select
    }
    if (newMode === 'style' || newMode === 'combined') {
      enableStylePicker(doc);
    }
    setEditMode(newMode);
  };

  // Region selection handlers
  const getPercentCoords = (e) => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  };

  const handleRegionMouseDown = (e) => {
    if (!regionMode) return;
    e.preventDefault();
    const coords = getPercentCoords(e);
    if (!coords) return;
    setIsDrawing(true);
    setDrawStart(coords);
    setDrawCurrent(coords);
    setSelectedRegion(null);
  };

  const handleRegionMouseMove = (e) => {
    if (!isDrawing) return;
    const coords = getPercentCoords(e);
    if (coords) setDrawCurrent(coords);
  };

  const handleRegionMouseUp = () => {
    if (!isDrawing || !drawStart || !drawCurrent) {
      setIsDrawing(false);
      return;
    }
    setIsDrawing(false);
    const rx = Math.min(drawStart.x, drawCurrent.x);
    const ry = Math.min(drawStart.y, drawCurrent.y);
    const rw = Math.abs(drawCurrent.x - drawStart.x);
    const rh = Math.abs(drawCurrent.y - drawStart.y);
    if (rw > 2 && rh > 2) {
      const region = { x: rx, y: ry, width: rw, height: rh };
      setSelectedRegion(region);
      onRegionSelect?.(region);
    }
    setDrawStart(null);
    setDrawCurrent(null);
    setRegionMode(false);
  };

  const getDrawingStyle = () => {
    if (!drawStart || !drawCurrent) return null;
    const x = Math.min(drawStart.x, drawCurrent.x);
    const y = Math.min(drawStart.y, drawCurrent.y);
    const w = Math.abs(drawCurrent.x - drawStart.x);
    const h = Math.abs(drawCurrent.y - drawStart.y);
    return { left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` };
  };

  if (!slide?.html_source) {
    return (
      <div className="aspect-video bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center text-muted-foreground">
        <span>No HTML source available</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header + toolbar — rendered above the slide */}
      <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-muted-foreground font-medium truncate">
            Slide {slideIndex + 1}{slide?.title ? ` — ${slide.title}` : ''}
          </span>
          {onDelete && (
            <button
              onClick={onDelete}
              className="text-muted-foreground hover:text-red-500 transition-colors p-0.5 rounded"
              title={`Delete slide ${slideIndex + 1}`}
              disabled={readOnly}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {typeof totalSlides === 'number' && (
            <span className="text-[10px] text-muted-foreground">{slideIndex + 1} of {totalSlides}</span>
          )}
        </div>
      {!readOnly && <div className="flex justify-end gap-1 flex-wrap">
        {editMode !== 'view' && (
          <>
            <Button size="sm" variant="outline" className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
              onClick={handleUndo} disabled={historyIdx <= 0} title="Undo (Ctrl+Z)">
              <Undo2 className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 text-red-500 hover:text-red-600"
              onClick={handleCancel} title="Cancel all edits">
              <X className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {editMode === 'view' && onRegionSelect && (
          <Button
            size="sm"
            variant={regionMode ? 'default' : 'outline'}
            className={`shadow-md ${regionMode ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300'}`}
            onClick={() => {
              if (regionMode) {
                setRegionMode(false);
              } else {
                setRegionMode(true);
                setSelectedRegion(null);
                onRegionSelect?.(null);
              }
            }}
            title="Select a region to edit"
          >
            <Crosshair className="w-3.5 h-3.5 mr-1.5" />
            {regionMode ? 'Cancel' : 'Select Region'}
          </Button>
        )}
        {editMode === 'view' && (onFactCheck || onDriveFactCheck) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
                disabled={factChecking}
              >
                {factChecking ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
                {factChecking ? 'Checking...' : 'Fact Check'}
                <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onFactCheck && (
                <DropdownMenuItem
                  onClick={async () => {
                    const text = iframeRef.current?.contentDocument?.body?.innerText || '';
                    if (!text.trim()) return;
                    setFactChecking(true);
                    try { await onFactCheck(slideIndex, text); } finally { setFactChecking(false); }
                  }}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Web Search
                </DropdownMenuItem>
              )}
              {onDriveFactCheck && (
                <DropdownMenuItem onClick={() => onDriveFactCheck()}>
                  <HardDrive className="w-4 h-4 mr-2" />
                  Drive Data
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {externalEditMode === undefined && (
        <Button
          size="sm"
          variant={editMode !== 'view' ? 'default' : 'outline'}
          className={`shadow-md ${editMode !== 'view' ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300'}`}
          onClick={() => {
            if (editMode !== 'view') {
              // Save and exit edit mode
              handleModeSwitch(editMode);
            } else {
              // Enter combined edit mode — text + style together
              handleModeSwitch('combined');
            }
          }}
        >
          {editMode !== 'view' ? (
            <><Check className="w-3.5 h-3.5 mr-1.5" /> Done</>
          ) : (
            <><Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit</>
          )}
        </Button>
        )}
        {editMode !== 'view' && (
          <>
            {/* r117: stacking is the user's call, not only the drop
                heuristic's. */}
            <Button
              size="sm"
              variant="outline"
              className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
              onClick={() => handleRestack('front')}
              title="Bring selected element to front (Ctrl+])"
            >
              <ChevronsUp className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
              onClick={() => handleRestack('back')}
              title="Send selected element to back (Ctrl+[)"
            >
              <ChevronsDown className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
              onClick={handleLiftOut}
              title="Lift the selected element out of its container so it is no longer clipped (keeps its position)"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={handleDeleteSelected}
              title="Delete selected element (Del)"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        {editMode === 'view' && slide?.html_source && onQaSlide && (
          <Button
            size="sm"
            variant="outline"
            className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
            onClick={async () => {
              if (qaRunning) return;
              setQaRunning(true);
              try { await onQaSlide(slideIndex, slide.html_source, slide.preview_url); }
              finally { setQaRunning(false); }
            }}
            disabled={qaRunning}
            title="Scan this slide for visual issues (overlaps, overflow, alignment) and suggest fixes"
          >
            {qaRunning ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5 text-purple-500" />}
            {qaRunning ? 'Scanning...' : 'QA Slide'}
          </Button>
        )}
        {editMode === 'view' && slide?.html_source && onSlideVariation && !pendingVariation && (
          <Button
            size="sm"
            variant="outline"
            className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
            onClick={async () => {
              if (variationRunning) return;
              setVariationRunning(true);
              try {
                const result = await onSlideVariation(slideIndex, slide.html_source);
                if (result?.html) {
                  // Store the variation but DO NOT update the slide yet.
                  // The original keeps rendering; the variation overlays via
                  // the compare iframe. User confirms with Keep before the
                  // slide actually changes.
                  setPendingVariation({
                    html: result.html,
                    preview_url: result.preview_url || '',
                    original: { html: slide.html_source, preview_url: slide.preview_url },
                  });
                  setCompareSlider(50);
                }
              } finally { setVariationRunning(false); }
            }}
            disabled={variationRunning}
            title="Generate a different design variation of this slide (same content, brand-locked)"
          >
            {variationRunning ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5 text-cyan-500" />}
            {variationRunning ? 'Designing...' : 'Variation'}
          </Button>
        )}
        {editMode === 'view' && canUndo && onUndo && !pendingVariation && (
          <Button
            size="sm"
            variant="outline"
            className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
            onClick={onUndo}
            title="Undo the last chat-driven edit on this slide"
          >
            <Undo2 className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
            Undo
          </Button>
        )}
        {editMode === 'view' && canRedo && onRedo && !pendingVariation && (
          <Button
            size="sm"
            variant="outline"
            className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
            onClick={onRedo}
            title="Redo the edit you just undid on this slide"
          >
            <Redo2 className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
            Redo
          </Button>
        )}
        {editMode === 'view' && slide?.html_source && (
          <Button
            size="sm"
            variant="outline"
            className="shadow-md bg-white/95 hover:bg-slate-100 border border-slate-200 hover:border-slate-300"
            onClick={() => setShowCode(true)}
            title="View slide HTML source"
          >
            <Code2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>}
      </div>

      <div ref={containerRef} className="aspect-video relative rounded-lg border border-slate-200 dark:border-slate-700 bg-white overflow-hidden">

      {/* Mode indicator */}
      {(editMode === 'text' || editMode === 'combined') && (
        <div className="absolute top-2 left-2 z-20 px-2 py-1 bg-blue-500 text-white text-xs rounded-md shadow-md">
          Click text to edit · Drag to move · Alt+click selects underneath · Esc deselects · Del deletes
        </div>
      )}

      {/* r126: the floating font toolbar that lived here is retired. It was
          built when no style panel rendered in production; the docked panel
          now owns typography, with the loaded-font list and the
          gradient-text guard carried over. One styling surface, not two
          disagreeing ones. */}
      {editMode === 'style' && (
        <div className="absolute top-2 left-2 z-20 px-2 py-1 bg-indigo-500 text-white text-xs rounded-md shadow-md">
          Click any element to inspect
        </div>
      )}
      {regionMode && (
        <div className="absolute top-2 left-2 z-20 px-2 py-1 bg-amber-500 text-white text-xs rounded-md shadow-md">
          Drag to select a region
        </div>
      )}

      {/* Scaled iframe */}
      <div
        style={{
          width: '1280px',
          height: '720px',
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <iframe
          ref={iframeRef}
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '1280px', height: '720px', border: 'none' }}
          title={`Slide ${slideIndex + 1}`}
        />
        {/* Variation compare overlay — second iframe rendering the new design.
            Clipped via clip-path so only the right side of the slider shows.
            Keep / Discard appear in a floating bottom bar (rendered outside
            this scaled wrapper so they stay readable). */}
        {pendingVariation && (
          <iframe
            ref={compareIframeRef}
            sandbox="allow-scripts allow-same-origin"
            srcDoc={pendingVariation.html}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '1280px',
              height: '720px',
              border: 'none',
              clipPath: `inset(0 0 0 ${compareSlider}%)`,
              pointerEvents: 'none',
            }}
            title="Variation compare"
          />
        )}
      </div>

      {/* Variation slider divider — vertical line + drag handle, anchored
          to the slide canvas. Sits above the iframes. */}
      {pendingVariation && (
        <div
          className="absolute top-0 bottom-0 z-20 pointer-events-none"
          style={{ left: `${compareSlider}%` }}
        >
          <div className="absolute top-0 bottom-0 w-px bg-cyan-500 left-1/2 -translate-x-1/2 shadow-[0_0_8px_rgba(6,182,212,0.6)]" />
          <div
            role="slider"
            aria-label="Compare original vs variation"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(compareSlider)}
            tabIndex={0}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border-2 border-cyan-500 shadow-lg flex items-center justify-center cursor-ew-resize pointer-events-auto select-none"
            onMouseDown={(e) => {
              compareDraggingRef.current = true;
              const startX = e.clientX;
              const parent = e.currentTarget.closest('.relative');
              const rect = parent?.getBoundingClientRect();
              const handleMove = (ev) => {
                if (!compareDraggingRef.current || !rect) return;
                const pct = Math.max(0, Math.min(100, ((ev.clientX - rect.left) / rect.width) * 100));
                setCompareSlider(pct);
              };
              const handleUp = () => {
                compareDraggingRef.current = false;
                window.removeEventListener('mousemove', handleMove);
                window.removeEventListener('mouseup', handleUp);
              };
              window.addEventListener('mousemove', handleMove);
              window.addEventListener('mouseup', handleUp);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') setCompareSlider(p => Math.max(0, p - 5));
              else if (e.key === 'ArrowRight') setCompareSlider(p => Math.min(100, p + 5));
            }}
          >
            <div className="flex items-center gap-px">
              <ChevronDown className="w-3 h-3 text-cyan-600 -rotate-90" />
              <ChevronDown className="w-3 h-3 text-cyan-600 rotate-90" />
            </div>
          </div>
        </div>
      )}

      {/* Variation labels showing which side is what */}
      {pendingVariation && (
        <>
          <div className="absolute top-2 left-2 z-20 px-2 py-0.5 rounded bg-slate-900/80 text-white text-xs font-medium shadow-md backdrop-blur-sm pointer-events-none">
            Original
          </div>
          <div className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded bg-cyan-600/90 text-white text-xs font-medium shadow-md backdrop-blur-sm pointer-events-none">
            Variation
          </div>
        </>
      )}

      {/* Floating bottom bar — Keep / Discard for the pending variation. */}
      {pendingVariation && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-white rounded-full shadow-xl border border-slate-200 px-2 py-1.5">
          <span className="text-xs text-slate-500 px-2">Drag the slider to compare</span>
          <Button
            size="sm"
            className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm h-8 px-3"
            onClick={() => {
              // Record the original to undo history, then commit variation.
              if (onVariationKept) onVariationKept(slideIndex, pendingVariation.original);
              onSlideUpdate?.(slideIndex, {
                ...slide,
                html_source: pendingVariation.html,
                preview_url: pendingVariation.preview_url || slide.preview_url,
                _needsRescreenshot: !pendingVariation.preview_url,
              });
              setPendingVariation(null);
            }}
          >
            <Check className="w-3.5 h-3.5 mr-1" />
            Keep
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full h-8 px-3 border-slate-300 hover:bg-slate-100"
            onClick={() => setPendingVariation(null)}
          >
            <X className="w-3.5 h-3.5 mr-1 text-rose-500" />
            Discard
          </Button>
        </div>
      )}

      {/* Region selection overlay */}
      {regionMode && (
        <div
          className="absolute inset-0 z-10"
          style={{ cursor: 'crosshair' }}
          onMouseDown={handleRegionMouseDown}
          onMouseMove={handleRegionMouseMove}
          onMouseUp={handleRegionMouseUp}
          onMouseLeave={() => { if (isDrawing) handleRegionMouseUp(); }}
        >
          {isDrawing && getDrawingStyle() && (
            <div className="absolute border-2 border-amber-500 bg-amber-500/20 rounded" style={getDrawingStyle()} />
          )}
        </div>
      )}

      {/* Selected region highlight */}
      {selectedRegion && !regionMode && (
        <div className="absolute z-10 border-2 border-amber-500 bg-amber-500/10 rounded pointer-events-none"
          style={{ left: `${selectedRegion.x}%`, top: `${selectedRegion.y}%`, width: `${selectedRegion.width}%`, height: `${selectedRegion.height}%` }}
        >
          <button
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center pointer-events-auto hover:bg-red-600 shadow"
            onClick={() => { setSelectedRegion(null); onRegionSelect?.(null); }}
          >×</button>
        </div>
      )}

      </div>{/* end aspect-video slide container */}

      {/* HTML Source Code Viewer */}
      {showCode && slide?.html_source && (
        <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCode(false)}>
          <div className="bg-slate-900 rounded-lg shadow-2xl w-full h-full max-w-full max-h-full flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
              <span className="text-sm font-medium text-slate-300">Slide {slideIndex + 1} — HTML Source ({Math.round(slide.html_source.length / 1024)}KB)</span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-slate-300 hover:text-white"
                  onClick={() => {
                    navigator.clipboard.writeText(slide.html_source);
                    setCodeCopied(true);
                    setTimeout(() => setCodeCopied(false), 2000);
                  }}
                >
                  {codeCopied ? <><CheckCheck className="w-3.5 h-3.5 mr-1 text-green-400" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-slate-300 hover:text-white"
                  onClick={() => {
                    const blob = new Blob([slide.html_source], { type: 'text/html' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `slide_${slideIndex + 1}.html`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Download .html
                </Button>
                <button className="text-slate-400 hover:text-white" onClick={() => setShowCode(false)}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs text-green-300 font-mono leading-relaxed select-all whitespace-pre-wrap break-all">
              {slide.html_source}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
});

export default HtmlSlideEditor;
